import { useState, FC, useEffect, useRef, MutableRefObject, useCallback, useMemo, } from 'react';
import { FormControl, TextField, Chip, Paper, styled, ListItem, ListItemIcon, Checkbox,
AutocompleteRenderOptionState, 
Button,
Box} from '@mui/material';
import { Autocomplete, FilterOptionsState } from "@mui/material";
import { AutocompleteChangeReason, createFilterOptions } from '@mui/material/Autocomplete';
import { isTemplateExpression, nodeModuleNameResolver } from 'typescript';
import { gridColumnsTotalWidthSelector } from '@mui/x-data-grid';
import { deepEqual } from '../utilities/utils';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { difference } from 'lodash';

const filter = createFilterOptions<SearchableDropdownOption>();

interface SearchableDropdownProps {
  label: string;
  value: SearchableDropdownOption[];
  setValue: React.Dispatch<React.SetStateAction<SearchableDropdownOption[]>>;
  options: SearchableDropdownOption[];
  placeholder?: string;
  freeSolo?: boolean;
  allowNewEntries?: boolean;
  colorDict?: { [key: string]: string };
  fullWidth?: boolean;
  multiple?: boolean;
  error?: boolean;
  forcePill?:boolean;
  helpText?: string;
  draggable?: boolean;
  startExpanded?: boolean;
  editable?: boolean;
  disabled?: boolean;
}

const SearchableDropdown: FC<SearchableDropdownProps> = ({ 
  label, value, setValue, options, placeholder, 
  freeSolo = false, 
  allowNewEntries = false, 
  colorDict = {}, 
  fullWidth = true,
  multiple = true,
  error = false,
  forcePill = false,
  helpText = '',
  draggable = true,
  startExpanded = false,
  editable = true,
  disabled = false,
}) => {
    let selectAllOption: SearchableDropdownOption = { label: 'Select All', value: '*' };

    const isAllSelected = value.length === options.length;
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');

    const hardExpandCap = 1000; // Hard cap for expanding the dropdown list. This is to prevent performance issues with large lists (RNA-seq)
    const expandIncrement = 100; // Increment for expanding the dropdown list
    const [expandLimit, setExpandLimit] = useState(100);

    // Define a custom Autocomplete with dark styles
    const DarkAutocomplete = styled(Autocomplete)(({ theme }) => ({
        '& .MuiAutocomplete-inputRoot': {
        color: '#131313', // Text color
        '&.Mui-focused .MuiAutocomplete-input': {
            color: '#131313', // Text color when focused
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#131313', // Border color
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#131313', // Border color when hovered
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#131313', // Border color when focused
        },
        },
        '& .MuiAutocomplete-endAdornment': {
        '& .MuiButtonBase-root': {
            color: '#131313', // Icons (clear and dropdown arrow) color
        },
        },
    }));

    const filter = createFilterOptions<SearchableDropdownOption>({'limit': 500});

    const valueSet = useMemo(() => {
        const set = new Set(value.map(option => option.value));
        return set;
    }, [value]);

    const filterOptions = (
        options: SearchableDropdownOption[],
        params: FilterOptionsState<SearchableDropdownOption>,
        selectAll?: boolean
    ): SearchableDropdownOption[] => {
        // Filter out any option that has a null value 
        // options = options.filter(option => option.value !== null);
        let filtered = options;
        if (selectAll === undefined || !selectAll) {
            filtered = filter(options, params);
        }
        // Exclude selected values from options
        const filteredList = filtered.filter(option => option !== null &&!valueSet.has(option.value));
        return filteredList;
    };

    const handleSelectAllClick = () => {
        // Calculate filtered options based on the current search input
        const filteredOptions = filterOptions(options, { inputValue: searchInput, getOptionLabel: (option) => option !== undefined ? option.label : '' }, true);

        setValue(value.concat(filteredOptions)); // Exclude 'Select All' from the selection

        setDropdownOpen(false); // Close the dropdown
    };

    const renderOption = (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: SearchableDropdownOption,
        state: AutocompleteRenderOptionState
    ) => {
        return (
            <li {...props}>
                <div style={{ width: '100%' }}>
                    {option?.label}
                </div>
            </li>
        );
    };
    

    const maxDisplayed = 5;
    const [isExpanded, setIsExpanded] = useState((startExpanded) ? true : false);


    const CustomPaperComponent = (props: any) => {
        return (
            <Paper {...props} style={{ maxHeight: '450px', overflow: 'auto' }}>
               {multiple ? <Box width='100%' display={'flex'} alignContent={'center'}><Button style={{width:'100%'}} onMouseDown={()=>handleSelectAllClick()}>Select All</Button></Box> : <></>}
               {props.children}
            </Paper>
        );
    };
    
    // Effect to handle transition from multiple to single select
    useEffect(() => {
        if (!multiple && Array.isArray(value) && value.length > 1) {
            // Only update the value if there are more than one item
            setValue([value[0]]);
        }
    }, [multiple]);


    const reorder = (list: SearchableDropdownOption[], startIndex: number, endIndex: number): SearchableDropdownOption[] => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };
        
    // Ensure correct value format for Autocomplete based on 'multiple'
    const autocompleteValue = multiple ? value : (value.length > 0 ? value[0] : null);

    const handleChangeValue = (
        newValue: string | SearchableDropdownOption | (string | SearchableDropdownOption)[] | null,
        reason: AutocompleteChangeReason,
        event: React.SyntheticEvent<Element, Event>,
    ) => {
        if (reason === 'blur'){
            event.preventDefault()
        }
        setSearchInput('')
        if (!multiple ) {
            // Directly update for single selection
            if (newValue !== null && typeof newValue !== 'string') {
                setValue([newValue as SearchableDropdownOption]);
            } else {
                // Reset to empty array if newValue is null or a string
                setValue([]);
            }
        } else {
            // Updated logic for multiple selection respecting optionLimit
            const newValuesArray = Array.isArray(newValue) ? newValue : [newValue];
            const filteredNewValue = newValuesArray.filter(
                item => typeof item !== 'string'
            ) as SearchableDropdownOption[];
                setValue(filteredNewValue);
        }        
    }
    

    const getOptionLabel = (option: any) => {
        let returnedValue = ''
        if (option === undefined){
            return returnedValue
        }
        if (typeof option === 'string') {
            returnedValue = option
        }
        else if (option.label) {
            returnedValue = option.label
        }
        return returnedValue
    }

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
          return;
        }
    
        const newItems = reorder(
          value,
          result.source.index,
          result.destination.index
        );
    
        setValue(newItems);
    };

    const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
        // userSelect: 'none',
        border: '0px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '0px',
        margin: `0 0 0px 0`, // 4px space between items
        padding: '0px', // or even less like '4px' if the items are still too large
        // background: isDragging ? 'lightgrey' : 'white',
        // transition: 'background-color 0.25s ease-out',
        width: 'auto',
        // ...draggableStyle,
      });

    const getListStyle = (isDraggingOver: boolean) => ({
        background: 'transparent', // Changed from lightgrey to transparent
        padding: 0,
        // width: '100%',
        display: (draggable) ? 'block': 'contents',
        border: '0px solid rgba(0, 0, 0, 0.12)', // A thin outline for the container
        borderRadius: '0px', // Optional rounded corners
        maxHeight: '100%',
        overflowY: 'auto',
    });
      

    return (
    <FormControl style={{ width: '100%' }}>
        <Autocomplete
            multiple={multiple}
            value={autocompleteValue}
            disabled={disabled}
            open={dropdownOpen}
            onOpen={() => setDropdownOpen(true)}
            onClose={() => setDropdownOpen(false)}
            disableClearable={false}
            freeSolo={freeSolo}
            selectOnFocus
            getOptionLabel={getOptionLabel}
            clearOnBlur
            readOnly={!editable}
            onBlur={()=> setSearchInput('')}
            options={options} // This now uses the original options list
            filterOptions={filterOptions} // Applies custom filtering logic including exclusion of selected items
            onChange={(event, newValue, reason) => handleChangeValue(newValue, reason, event)}
            isOptionEqualToValue={(option, value) => option !== undefined && value !== undefined && option.value === value.value}
            autoHighlight
            sx={{
                width: '100%',
                // '& .MuiAutocomplete-inputRoot': {
                //   color: '#131313', // Text color
                //   '&.Mui-focused .MuiAutocomplete-input': {
                //     color: '#131313', // Text color when focused
                //   },
                //   '& .MuiOutlinedInput-notchedOutline': {
                //     borderColor: '#131313', // Border color
                //   },
                //   '&:hover .MuiOutlinedInput-notchedOutline': {
                //     borderColor: '#131313', // Border color when hovered
                //   },
                //   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                //     borderColor: '#131313', // Border color when focused
                //   },
                // },
                // '& .MuiAutocomplete-endAdornment': {
                //   '& .MuiButtonBase-root': {
                //     color: '#131313', // Icons (clear and dropdown arrow) color
                //   },
                // },
               
                // '& .MuiAutocomplete-popupIndicator': {
                //   color: '#131313', // Arrow icon color
                // },
                // '& .MuiAutocomplete-clearIndicator': {
                //   color: '#131313', // Clear icon color
                // },

            }}
            renderOption={renderOption}
            renderTags={(value, getTagProps) => {
                const visibleItems = value.slice(0, (!isExpanded) ? maxDisplayed : expandLimit);
                const hiddenItems = value.slice( (!isExpanded) ? maxDisplayed : expandLimit);
            
                return (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable" isDropDisabled={!draggable}>
                            {(provided, snapshot) => (
                                <Box ref={provided.innerRef} sx={getListStyle(snapshot.isDraggingOver)}>
                                    {(isExpanded && value.length < expandLimit) || value.length <= maxDisplayed ? (
                                        // Show all tags when expanded or when total items are below or equal to the threshold
                                        <>
                                            {value.map((option, index) => (
                                                <Draggable isDragDisabled={!draggable} key={`${option.id}-${option.label}`} draggableId={option.label} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div 
                                                            ref={provided.innerRef} 
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            // style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                        >
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                label={option.label}
                                                                onDelete={(editable) ? () => {
                                                                    const newValues = value.filter(val => val.value !== option.value);
                                                                    setValue(newValues);
                                                                } : undefined}
                                                                style={{backgroundColor: colorDict[option.id? option.id : option.value], margin: '0.2rem'}}
                                                            />
                                                         </div>
                                                     )}
                                                 </Draggable>
                                            
                                            ))}
                                            {isExpanded && visibleItems.length > 0 && (
                                                <Chip
                                                    label="Collapse"
                                                    clickable
                                                    onClick={() => {setIsExpanded(false); setExpandLimit(100)}} // Reset the expand limit
                                                    variant="outlined"
                                                    style={{margin: (snapshot.isDraggingOver)? '39px 0.2rem 0.2rem' : '0.2rem'}}
                                                />
                                            )}    
                                        </>
                                    ) : (
                                        // When not expanded and items exceed the threshold
                                        <>
                                            {visibleItems.map((option, index) => (
                                                <Draggable isDragDisabled={!draggable} key={`${option.id}-${option.label}`} draggableId={option.label} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div 
                                                            ref={provided.innerRef} 
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            // style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                                        >
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                label={option.label}
                                                                onDelete={(editable) ? () => {
                                                                    const newValues = value.filter(val => val.value !== option.value);
                                                                    setValue(newValues);
                                                                }: undefined}
                                                                style={{backgroundColor: colorDict[option.id ? option.id : option.value], margin: '0.2rem'}}
                                                            />
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            <Chip
                                                label={`+${hiddenItems.length} more`}
                                                clickable
                                                onClick={() => {
                                                    if (!isExpanded) {   
                                                        setIsExpanded(true)
                                                    }
                                                    else {
                                                        if (expandLimit < hardExpandCap) { // Prevent expanding beyond the hard cap
                                                            setExpandLimit(prev => prev + expandIncrement); // only expand in increments, prevents the user from having a massive scroll bar accidently
                                                        }
                                                    }
                                                }}
                                                variant="outlined"
                                                style={{margin: (snapshot.isDraggingOver)? '39px 0.2rem 0.2rem' : '0.2rem'}}
                                            />
                                             
                                            {visibleItems.length > maxDisplayed ? (
                                                <Chip
                                                    label="Collapse"
                                                    clickable
                                                    onClick={() => {setIsExpanded(false); setExpandLimit(100)}} // Reset the expand limit
                                                    variant="outlined"
                                                    style={{margin: (snapshot.isDraggingOver)? '39px 0.2rem 0.2rem' : '0.2rem'}}
                                                />
                                            )
                                            : <></>}           
                                        </>
                                    )}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </DragDropContext>
                );
            }}
            
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth={fullWidth ? true : false}
                    label={placeholder ? placeholder : ''}
                    error={error} 
                    helperText={helpText}
                    InputProps={{
                        ...params.InputProps,
                        // type: 'search',
                        style: error ? { borderColor: 'red', borderWidth: '2px' } : {},
                        
                    }}
                    onChange={(e) => setSearchInput(e.target.value)} // Track search input
                />
            )}
            
            PaperComponent={CustomPaperComponent}

        />
    </FormControl>
)};

export default SearchableDropdown;